import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';
import { useGetLegalTransactionTypes } from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetUsers } from '../hooks/reactQuerys/UserQueries';
import { useCourts } from '../Aemter/hooks';

type Aggregate = 'count' | 'sum' | 'avg' | 'min' | 'max';
type ChartType = 'bar' | 'pie' | 'line';
type Ordering = 'value-desc' | 'value-asc' | 'label-asc' | 'label-desc';
type FilterType = 'date' | 'int' | 'flag' | 'string';
type OperatorId =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'like'
  | 'notnull'
  | 'isnull';

interface ISourceField {
  id: string;
  label: string;
  expertOnly?: boolean;
}
interface ISourceFilterField {
  id: string;
  label: string;
  type: FilterType;
  expertOnly?: boolean;
  expertOperators?: OperatorId[];
}
interface ISource {
  id: string;
  label: string;
  groupable: ISourceField[];
  filterable: ISourceFilterField[];
  aggregateFields: ISourceField[];
}
interface IOperatorDef {
  id: OperatorId;
  label: string;
  needsValue: boolean;
}
interface ISourcesResponse {
  sources: ISource[];
  aggregates: Aggregate[];
  orderings: Ordering[];
  chartTypes: ChartType[];
  operators: IOperatorDef[];
}
interface IFilter {
  key: string;
  operator?: OperatorId;
  value?: string | number | boolean;
}
interface ICustomReport {
  idCustomReport: number;
  Title: string;
  Description?: string | null;
  Source: string;
  Aggregate: Aggregate;
  AggregateField?: string | null;
  GroupBy?: string | null;
  Filters: IFilter[];
  OrderBy: Ordering;
  LimitRows: number;
  ChartType: ChartType;
  IsShared?: boolean;
}
interface IPreviewResponse {
  items: Array<{ label: string; value: number }>;
  chartType: ChartType;
}

const PIE_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#c2185b',
  '#7b1fa2',
  '#0097a7',
  '#5d4037',
  '#455a64',
];

const AGG_LABEL: Record<Aggregate, string> = {
  count: 'Anzahl',
  sum: 'Summe',
  avg: 'Durchschnitt',
  min: 'Minimum',
  max: 'Maximum',
};

const ORDER_LABEL: Record<Ordering, string> = {
  'value-desc': 'Wert absteigend',
  'value-asc': 'Wert aufsteigend',
  'label-asc': 'Label A→Z',
  'label-desc': 'Label Z→A',
};

const CHART_LABEL: Record<ChartType, string> = {
  bar: 'Balken',
  pie: 'Kreis',
  line: 'Linie',
};

const emptyDraft = (): ICustomReport => ({
  idCustomReport: -1,
  Title: '',
  Description: '',
  Source: '',
  Aggregate: 'count',
  AggregateField: null,
  GroupBy: null,
  Filters: [],
  OrderBy: 'value-desc',
  LimitRows: 50,
  ChartType: 'bar',
  IsShared: false,
});

/**
 * Eigene Reports / Expertenmodus.
 *
 * Architektur (siehe Backend `app/logic/customreport.py:SOURCES`):
 *   - User wählt eine **Datenquelle** (Vorgänge / Maßnahmen / Mandanten / Rechnungen).
 *   - **Aggregat**: Anzahl (count) oder Summe/Durchschnitt/Min/Max auf einem
 *     vordefinierten Feld der Quelle.
 *   - **Gruppierung**: Klartext-Spalten der Quelle (z. B. „Mitarbeiter").
 *   - **Filter**: dynamisch hinzufügbar; pro Filter wird der Typ aus der
 *     Quelle gelesen (Datum / Integer / Flag) und der passende Editor
 *     gerendert.
 *   - **Sortierung / Limit / Diagrammtyp**: pro Report einstellbar.
 *
 * Das Backend whitelistet alle Spalten, sodass kein freier SQL-Inhalt
 * möglich ist — auch ohne Drag-and-Drop ist das mächtig genug für die
 * meisten Auswertungs-Wünsche.
 */
export const CustomReportsView: React.FC = () => {
  const queryClient = useQueryClient();
  const { legalTransactionTypes } = useGetLegalTransactionTypes();
  const { users } = useGetUsers();
  const { courts } = useCourts();

  const { data: sourcesData, isLoading: isLoadingSources } =
    useQuery<ISourcesResponse>({
      refetchOnWindowFocus: false,
      queryKey: ['/customreport/sources'],
      queryFn: () => customFetcher({ endpoint: '/customreport/sources' }),
    });

  const { data: reports, isLoading: isLoadingReports } = useQuery<
    ICustomReport[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['/customreport'],
    queryFn: () => customFetcher({ endpoint: '/customreport' }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['/customreport'] });

  const { mutateAsync: createReport, isPending: isCreating } = useMutation({
    mutationFn: (input: Partial<ICustomReport>) =>
      customFetcher({
        endpoint: '/customreport',
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
  const { mutateAsync: updateReport, isPending: isUpdating } = useMutation({
    mutationFn: (input: { id: number; data: Partial<ICustomReport> }) =>
      customFetcher({
        endpoint: `/customreport/${input.id}`,
        method: 'PUT',
        body: input.data,
      }),
    onSuccess: invalidate,
  });
  const { mutateAsync: deleteReport } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/customreport/${id}`,
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
  const { mutateAsync: previewAdHoc } = useMutation({
    mutationFn: (cfg: Partial<ICustomReport>) =>
      customFetcher({
        endpoint: '/customreport/preview',
        method: 'POST',
        body: cfg,
      }) as Promise<IPreviewResponse>,
  });

  // -------- Builder-Dialog-State --------
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<ICustomReport>(emptyDraft());
  const [previewData, setPreviewData] = useState<IPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Expertenmodus: blendet im Builder die `expertOnly`-Felder ein und
  // erlaubt freie Operator-Wahl bei Filtern, die `expertOperators` deklarieren.
  const [expertMode, setExpertMode] = useState(false);

  // -------- Anzeige-State (für gespeicherte Reports im Hauptbereich) --------
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const selectedSource = useMemo(
    () => sourcesData?.sources.find(s => s.id === draft.Source) ?? null,
    [sourcesData, draft.Source]
  );

  // Sichtbare Felder/Filter abhängig vom Expertenmodus.
  const visibleGroupable = useMemo(
    () =>
      (selectedSource?.groupable || []).filter(
        g => expertMode || !g.expertOnly
      ),
    [selectedSource, expertMode]
  );
  const visibleFilterable = useMemo(
    () =>
      (selectedSource?.filterable || []).filter(
        f => expertMode || !f.expertOnly
      ),
    [selectedSource, expertMode]
  );
  const visibleAggregateFields = useMemo(
    () =>
      (selectedSource?.aggregateFields || []).filter(
        f => expertMode || !f.expertOnly
      ),
    [selectedSource, expertMode]
  );

  const openCreate = () => {
    setDraft(emptyDraft());
    setPreviewData(null);
    setPreviewError(null);
    setEditorOpen(true);
  };

  const openEdit = (r: ICustomReport) => {
    setDraft({ ...r });
    setPreviewData(null);
    setPreviewError(null);
    setEditorOpen(true);
  };

  const remove = async (r: ICustomReport) => {
    if (!window.confirm(`Report „${r.Title}" wirklich löschen?`)) return;
    await deleteReport(r.idCustomReport);
    if (selectedReportId === r.idCustomReport) setSelectedReportId(null);
  };

  // Beim Wechsel der Source: Aggregate + GroupBy + Filter resetten, weil sie
  // sich auf vorherige Quelle beziehen.
  const handleSourceChange = (sourceId: string) => {
    setDraft({
      ...draft,
      Source: sourceId,
      Aggregate: 'count',
      AggregateField: null,
      GroupBy: null,
      Filters: [],
    });
  };

  const addFilter = () => {
    if (!selectedSource?.filterable.length) return;
    const firstVisible = visibleFilterable[0];
    if (!firstVisible) return;
    setDraft({
      ...draft,
      Filters: [...draft.Filters, { key: firstVisible.id }],
    });
  };

  const updateFilterAt = (idx: number, patch: Partial<IFilter>) => {
    const next = [...draft.Filters];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, Filters: next });
  };

  const removeFilterAt = (idx: number) => {
    const next = [...draft.Filters];
    next.splice(idx, 1);
    setDraft({ ...draft, Filters: next });
  };

  // Live-Vorschau: bei jeder relevanten Änderung wird die Konfiguration
  // ans Backend geschickt. Ein kleiner Debounce verhindert Flooding.
  useEffect(() => {
    if (!editorOpen || !draft.Source) return;
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      previewAdHoc(draft)
        .then(d => setPreviewData(d))
        .catch(e =>
          setPreviewError(
            e instanceof Error ? e.message : 'Vorschau fehlgeschlagen.'
          )
        )
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [editorOpen, JSON.stringify(draft)]);

  const submit = async () => {
    if (!draft.Title.trim()) {
      window.alert('Bitte einen Titel eingeben.');
      return;
    }
    if (!draft.Source) {
      window.alert('Bitte eine Datenquelle wählen.');
      return;
    }
    if (draft.idCustomReport > 0) {
      await updateReport({ id: draft.idCustomReport, data: draft });
    } else {
      await createReport(draft);
    }
    setEditorOpen(false);
  };

  // -------- Anzeige eines gespeicherten Reports im Hauptbereich --------
  const { data: selectedData, isLoading: isExecuting } =
    useQuery<IPreviewResponse>({
      enabled: !!selectedReportId,
      refetchOnWindowFocus: false,
      queryKey: ['/customreport/execute', selectedReportId],
      queryFn: () =>
        customFetcher({
          endpoint: `/customreport/${selectedReportId}/execute`,
        }),
    });

  const selectedReport =
    reports?.find(r => r.idCustomReport === selectedReportId) ?? null;

  // Export — gleicher Stil wie bei den Vorlagen-Reports.
  const downloadFile = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const exportSelected = (
    report: ICustomReport | null,
    data: IPreviewResponse | undefined,
    format: 'json' | 'csv'
  ) => {
    if (!report || !data?.items?.length) return;
    const baseName = report.Title.replace(/[^a-z0-9-_]/gi, '_').toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === 'json') {
      const payload = {
        report: {
          idCustomReport: report.idCustomReport,
          title: report.Title,
          source: report.Source,
          aggregate: report.Aggregate,
          aggregateField: report.AggregateField,
          groupBy: report.GroupBy,
          filters: report.Filters,
          chartType: report.ChartType,
        },
        generatedAt: new Date().toISOString(),
        data,
      };
      downloadFile(
        JSON.stringify(payload, null, 2),
        `custom-${baseName}-${stamp}.json`,
        'application/json'
      );
      return;
    }
    const cols = Object.keys(data.items[0]);
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      cols.join(';'),
      ...data.items.map(row =>
        cols
          .map(c => escape((row as unknown as Record<string, unknown>)[c]))
          .join(';')
      ),
    ];
    downloadFile(
      lines.join('\n'),
      `custom-${baseName}-${stamp}.csv`,
      'text/csv'
    );
  };

  const renderChart = (data: IPreviewResponse | null, ct: ChartType) => {
    if (!data?.items?.length) {
      return (
        <Alert severity='info' sx={{ mt: 2 }}>
          Keine Daten für diese Konfiguration.
        </Alert>
      );
    }
    const points = data.items.map(d => ({ ...d, value: Number(d.value) }));
    if (ct === 'pie') {
      return (
        <ResponsiveContainer width='100%' height={320}>
          <PieChart>
            <Pie
              data={points}
              dataKey='value'
              nameKey='label'
              cx='50%'
              cy='50%'
              outerRadius={110}
              label
            >
              {points.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Legend />
            <RechartsTooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (ct === 'line') {
      return (
        <ResponsiveContainer width='100%' height={320}>
          <LineChart data={points}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='label' />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line
              type='monotone'
              dataKey='value'
              stroke='#1976d2'
              strokeWidth={2}
              dot
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width='100%' height={320}>
        <BarChart data={points}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='label' />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Bar dataKey='value' fill='#1976d2' />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // -------- Rendering eines einzelnen Filter-Eintrags --------
  const renderFilterEditor = (f: IFilter, idx: number) => {
    if (!selectedSource) return null;
    const field = selectedSource.filterable.find(x => x.id === f.key);
    if (!field) return null;

    const valueEditor = (() => {
      if (field.type === 'flag') {
        return (
          <FormControlLabel
            label='Aktiv'
            control={
              <Switch
                size='small'
                checked={!!f.value}
                onChange={e => updateFilterAt(idx, { value: e.target.checked })}
              />
            }
          />
        );
      }
      if (field.type === 'date') {
        return (
          <TextField
            label='Wert'
            type='date'
            size='small'
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={(f.value as string) ?? ''}
            onChange={e => updateFilterAt(idx, { value: e.target.value })}
          />
        );
      }
      if (field.type === 'int') {
        // Häufig referenzieren int-Filter Entitäten — wenn der Filter-Key
        // erkannt wird, blenden wir einen Autocomplete ein. Sonst Zahleneingabe.
        if (f.key === 'idLegalTransactionType') {
          return (
            <Autocomplete
              size='small'
              options={legalTransactionTypes || []}
              getOptionLabel={t => t.LegalTransactionType}
              isOptionEqualToValue={(o, v) =>
                o.idLegalTransactionType === v.idLegalTransactionType
              }
              value={
                legalTransactionTypes?.find(
                  t => t.idLegalTransactionType === f.value
                ) ?? null
              }
              onChange={(_, v) =>
                updateFilterAt(idx, {
                  value: v ? v.idLegalTransactionType : undefined,
                })
              }
              renderInput={params => (
                <TextField {...params} label='Rechtsgeschäftsart' />
              )}
            />
          );
        }
        if (
          f.key === 'idEmployee' ||
          f.key === 'idAssignee' ||
          f.key === 'idNotary'
        ) {
          return (
            <Autocomplete
              size='small'
              options={users || []}
              getOptionLabel={u => `${u.FirstName} ${u.LastName}`}
              isOptionEqualToValue={(o, v) => o.idUser === v.idUser}
              value={users?.find(u => u.idUser === f.value) ?? null}
              onChange={(_, v) =>
                updateFilterAt(idx, { value: v ? v.idUser : undefined })
              }
              renderInput={params => (
                <TextField {...params} label='Mitarbeiter' />
              )}
            />
          );
        }
        if (f.key === 'idCourt') {
          return (
            <Autocomplete
              size='small'
              options={courts || []}
              getOptionLabel={c => c.Court}
              isOptionEqualToValue={(o, v) => o.idCourt === v.idCourt}
              value={courts?.find(c => c.idCourt === f.value) ?? null}
              onChange={(_, v) =>
                updateFilterAt(idx, { value: v ? v.idCourt : undefined })
              }
              renderInput={params => <TextField {...params} label='Gericht' />}
            />
          );
        }
        return (
          <TextField
            label='Wert'
            type='number'
            size='small'
            fullWidth
            value={(f.value as number | undefined) ?? ''}
            onChange={e =>
              updateFilterAt(idx, {
                value:
                  e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        );
      }
      return (
        <TextField
          label='Wert'
          size='small'
          fullWidth
          value={(f.value as string) ?? ''}
          onChange={e => updateFilterAt(idx, { value: e.target.value })}
        />
      );
    })();

    const supportsOperator =
      expertMode && field?.expertOperators && field.expertOperators.length > 0;
    const opDef = sourcesData?.operators.find(o => o.id === f.operator);

    return (
      <Stack
        key={idx}
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ mt: 1 }}
        flexWrap='wrap'
      >
        <TextField
          label='Filter'
          select
          size='small'
          value={f.key}
          onChange={e =>
            updateFilterAt(idx, {
              key: e.target.value,
              value: undefined,
              operator: undefined,
            })
          }
          sx={{ minWidth: 220 }}
        >
          {visibleFilterable.map(ff => (
            <MenuItem key={ff.id} value={ff.id}>
              {ff.label}
              {ff.expertOnly ? '  ⚙' : ''}
            </MenuItem>
          ))}
        </TextField>
        {supportsOperator && (
          <TextField
            label='Operator'
            select
            size='small'
            value={f.operator ?? ''}
            onChange={e =>
              updateFilterAt(idx, {
                operator: (e.target.value || undefined) as
                  | OperatorId
                  | undefined,
              })
            }
            sx={{ minWidth: 150 }}
          >
            <MenuItem value=''>– Standard –</MenuItem>
            {field.expertOperators!.map(opId => {
              const od = sourcesData?.operators.find(o => o.id === opId);
              return (
                <MenuItem key={opId} value={opId}>
                  {od?.label || opId}
                </MenuItem>
              );
            })}
          </TextField>
        )}
        {opDef && !opDef.needsValue ? (
          <Box sx={{ flex: 1, color: 'text.secondary', fontSize: 12 }}>
            (kein Wert nötig)
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 220 }}>{valueEditor}</Box>
        )}
        <IconButton size='small' onClick={() => removeFilterAt(idx)}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction='row' spacing={1} alignItems='center'>
            <BuildIcon color='primary' />
            <Typography variant='h6'>Eigene Reports (Expertenmodus)</Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Stellen Sie eigene Auswertungen aus Datenquelle, Aggregat,
            Gruppierung und Filtern zusammen — ohne SQL-Kenntnisse.
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Neuer Report
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper variant='outlined' sx={{ p: 1 }}>
            <Typography variant='subtitle2' sx={{ px: 1, py: 0.5 }}>
              Meine Reports
            </Typography>
            <Divider />
            {isLoadingReports || isLoadingSources ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : !reports?.length ? (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', p: 1.5 }}
              >
                Noch keine eigenen Reports. Klicken Sie auf „Neuer Report".
              </Typography>
            ) : (
              <Stack divider={<Divider />}>
                {reports.map(r => (
                  <Box
                    key={r.idCustomReport}
                    onClick={() => setSelectedReportId(r.idCustomReport)}
                    sx={{
                      p: 1,
                      cursor: 'pointer',
                      bgcolor:
                        selectedReportId === r.idCustomReport
                          ? 'action.selected'
                          : undefined,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant='body2' fontWeight={600}>
                          {r.Title}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {sourcesData?.sources.find(s => s.id === r.Source)
                            ?.label || r.Source}{' '}
                          · {AGG_LABEL[r.Aggregate]}
                        </Typography>
                      </Box>
                      <Stack direction='row'>
                        <Tooltip title='Bearbeiten'>
                          <IconButton
                            size='small'
                            onClick={e => {
                              e.stopPropagation();
                              openEdit(r);
                            }}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Löschen'>
                          <IconButton
                            size='small'
                            onClick={e => {
                              e.stopPropagation();
                              void remove(r);
                            }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card variant='outlined'>
            <CardContent>
              {!selectedReport ? (
                <Alert severity='info'>
                  Wählen Sie links einen gespeicherten Report aus oder klicken
                  Sie oben auf „Neuer Report".
                </Alert>
              ) : (
                <>
                  <Typography variant='h6'>{selectedReport.Title}</Typography>
                  {selectedReport.Description && (
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {selectedReport.Description}
                    </Typography>
                  )}
                  <Stack
                    direction='row'
                    spacing={0.5}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      size='small'
                      variant='outlined'
                      label={
                        sourcesData?.sources.find(
                          s => s.id === selectedReport.Source
                        )?.label || selectedReport.Source
                      }
                    />
                    <Chip
                      size='small'
                      variant='outlined'
                      label={AGG_LABEL[selectedReport.Aggregate]}
                    />
                    {selectedReport.GroupBy && (
                      <Chip
                        size='small'
                        variant='outlined'
                        label={`Gruppiert: ${selectedReport.GroupBy}`}
                      />
                    )}
                    {selectedReport.Filters?.map((f, i) => (
                      <Chip
                        key={i}
                        size='small'
                        variant='outlined'
                        label={`Filter: ${f.key}${f.value !== undefined ? ` = ${f.value}` : ''}`}
                      />
                    ))}
                  </Stack>
                  {isExecuting ? (
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    renderChart(selectedData ?? null, selectedReport.ChartType)
                  )}
                  <Stack
                    direction='row'
                    spacing={1}
                    justifyContent='flex-end'
                    sx={{ mt: 1 }}
                  >
                    <Button
                      size='small'
                      startIcon={<PlayArrowIcon fontSize='small' />}
                      onClick={() =>
                        queryClient.invalidateQueries({
                          queryKey: ['/customreport/execute', selectedReportId],
                        })
                      }
                    >
                      Erneut ausführen
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<DownloadIcon fontSize='small' />}
                      disabled={!selectedData?.items?.length}
                      onClick={() =>
                        exportSelected(selectedReport, selectedData, 'json')
                      }
                    >
                      JSON
                    </Button>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<DownloadIcon fontSize='small' />}
                      disabled={!selectedData?.items?.length}
                      onClick={() =>
                        exportSelected(selectedReport, selectedData, 'csv')
                      }
                    >
                      CSV
                    </Button>
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ============== Builder-Dialog ============== */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth='lg'
      >
        <DialogTitle>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <span>
              {draft.idCustomReport > 0 ? 'Report bearbeiten' : 'Neuer Report'}
            </span>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={expertMode}
                  onChange={e => setExpertMode(e.target.checked)}
                />
              }
              label={
                <Tooltip title='Schaltet zusätzliche Felder, Operatoren und Aggregat-Felder frei. Felder mit ⚙ sind Expert-only.'>
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <BuildIcon fontSize='small' />
                    <Typography variant='body2'>Expertenmodus</Typography>
                  </Box>
                </Tooltip>
              }
              labelPlacement='start'
              sx={{ ml: 0 }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Linke Spalte: Konfiguration */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <TextField
                  label='Titel'
                  size='small'
                  fullWidth
                  value={draft.Title}
                  onChange={e => setDraft({ ...draft, Title: e.target.value })}
                />
                <TextField
                  label='Beschreibung (optional)'
                  size='small'
                  fullWidth
                  value={draft.Description ?? ''}
                  onChange={e =>
                    setDraft({ ...draft, Description: e.target.value })
                  }
                />

                <TextField
                  label='Datenquelle'
                  select
                  size='small'
                  fullWidth
                  value={draft.Source}
                  onChange={e => handleSourceChange(e.target.value)}
                >
                  <MenuItem value=''>– Bitte wählen –</MenuItem>
                  {(sourcesData?.sources || []).map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedSource && (
                  <>
                    <Stack direction='row' spacing={1}>
                      <TextField
                        label='Aggregat'
                        select
                        size='small'
                        sx={{ minWidth: 160 }}
                        value={draft.Aggregate}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            Aggregate: e.target.value as Aggregate,
                            // count braucht kein Feld; bei sum/avg/etc.
                            // setzen wir das erste verfügbare als Default.
                            AggregateField:
                              e.target.value === 'count'
                                ? null
                                : draft.AggregateField ||
                                  visibleAggregateFields[0]?.id ||
                                  null,
                          })
                        }
                      >
                        {(sourcesData?.aggregates || []).map(a => (
                          <MenuItem key={a} value={a}>
                            {AGG_LABEL[a]}
                          </MenuItem>
                        ))}
                      </TextField>
                      {draft.Aggregate !== 'count' && (
                        <TextField
                          label='Aggregat-Feld'
                          select
                          size='small'
                          fullWidth
                          value={draft.AggregateField ?? ''}
                          onChange={e =>
                            setDraft({
                              ...draft,
                              AggregateField: e.target.value || null,
                            })
                          }
                          helperText={
                            !visibleAggregateFields.length
                              ? 'Keine numerischen Felder verfügbar — nur „Anzahl" möglich.'
                              : undefined
                          }
                          disabled={!visibleAggregateFields.length}
                        >
                          {visibleAggregateFields.map(f => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.label}
                              {f.expertOnly ? '  ⚙' : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>

                    <TextField
                      label='Gruppieren nach'
                      select
                      size='small'
                      fullWidth
                      value={draft.GroupBy ?? ''}
                      onChange={e =>
                        setDraft({
                          ...draft,
                          GroupBy: e.target.value || null,
                        })
                      }
                    >
                      <MenuItem value=''>– ohne (Gesamtwert) –</MenuItem>
                      {visibleGroupable.map(g => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.label}
                          {g.expertOnly ? '  ⚙' : ''}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Box>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography variant='subtitle2'>Filter</Typography>
                        <Button
                          size='small'
                          startIcon={<AddIcon fontSize='small' />}
                          onClick={addFilter}
                          disabled={!visibleFilterable.length}
                        >
                          Filter hinzufügen
                        </Button>
                      </Stack>
                      {draft.Filters.map((f, idx) =>
                        renderFilterEditor(f, idx)
                      )}
                      {draft.Filters.length === 0 && (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ display: 'block', mt: 1 }}
                        >
                          Keine Filter — alle Datensätze werden ausgewertet.
                        </Typography>
                      )}
                    </Box>

                    <Stack direction='row' spacing={1}>
                      <TextField
                        label='Sortierung'
                        select
                        size='small'
                        fullWidth
                        value={draft.OrderBy}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            OrderBy: e.target.value as Ordering,
                          })
                        }
                      >
                        {(sourcesData?.orderings || []).map(o => (
                          <MenuItem key={o} value={o}>
                            {ORDER_LABEL[o]}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label='Limit'
                        type='number'
                        size='small'
                        sx={{ width: 110 }}
                        value={draft.LimitRows}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            LimitRows: Number(e.target.value) || 50,
                          })
                        }
                      />
                      <TextField
                        label='Diagramm'
                        select
                        size='small'
                        sx={{ width: 130 }}
                        value={draft.ChartType}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            ChartType: e.target.value as ChartType,
                          })
                        }
                      >
                        {(sourcesData?.chartTypes || []).map(ct => (
                          <MenuItem key={ct} value={ct}>
                            {CHART_LABEL[ct]}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </>
                )}
              </Stack>
            </Grid>

            {/* Rechte Spalte: Vorschau */}
            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 2, minHeight: 320 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1 }}
                >
                  Live-Vorschau
                </Typography>
                {!draft.Source ? (
                  <Alert severity='info'>
                    Datenquelle wählen, um die Vorschau zu starten.
                  </Alert>
                ) : previewError ? (
                  <Alert severity='error'>{previewError}</Alert>
                ) : previewLoading ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  renderChart(previewData, draft.ChartType)
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            disabled={isCreating || isUpdating}
            onClick={submit}
          >
            {isCreating || isUpdating ? 'Speichere…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
